import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Trophy, Building2, TrendingUp, Search, AlertTriangle, Users2 } from "lucide-react";
import { SeguirCriterioButton } from "@/components/watchlist/SeguirCriterioButton";
import { AppShell } from "@/components/layout/AppShell";
import { EmptyState } from "@/components/intelligence/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { competitorsService } from "@/services/competitors.service";
import type { CompetidorOrden, CompetidorVentana } from "@/types";
import { formatCLP } from "@/lib/mock-data";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/competidores")({
  head: () => ({
    meta: [
      { title: "Inteligencia Competitiva — LicitaPro" },
      {
        name: "description",
        content: "Ranking de competidores, organismos dominados y análisis de adjudicaciones.",
      },
      { property: "og:title", content: "Inteligencia Competitiva — LicitaPro" },
      {
        property: "og:description",
        content: "Conoce a tus competidores antes de presentar oferta.",
      },
    ],
  }),
  component: CompetidoresPage,
});

const VENTANAS: Array<{ value: CompetidorVentana; label: string }> = [
  { value: "30", label: "Últimos 30 días" },
  { value: "90", label: "Últimos 90 días" },
  { value: "180", label: "Últimos 180 días" },
  { value: "365", label: "Último año" },
  { value: "todo", label: "Todo el histórico" },
];

const ORDENES: Array<{ value: CompetidorOrden; label: string }> = [
  { value: "share", label: "Mayor monto adjudicado" },
  { value: "adjudicadas", label: "Más adjudicaciones" },
  { value: "reciente", label: "Más recientes" },
];

type TipoCompetidores = "licitacion" | "compra-agil" | "todos";

const TIPOS: Array<{ value: TipoCompetidores; label: string }> = [
  { value: "licitacion", label: "Licitaciones" },
  { value: "compra-agil", label: "Compra Ágil" },
  { value: "todos", label: "Todos" },
];

function CompetidoresPage() {
  const [ventana, setVentana] = useState<CompetidorVentana>("365");
  const [orden, setOrden] = useState<CompetidorOrden>("share");
  const [tipo, setTipo] = useState<TipoCompetidores>("licitacion");
  const [search, setSearch] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [page, setPage] = useState(1);

  const filtros = { ventana, orden, tipo, search: busqueda || undefined, page, pageSize: 25 };

  const ranking = useQuery({
    queryKey: ["competidores", filtros],
    queryFn: () => competitorsService.list(filtros),
    staleTime: 5 * 60_000,
  });

  const organismos = useQuery({
    queryKey: ["competidores-organismos", ventana, tipo],
    queryFn: () => competitorsService.organismos({ ventana, tipo }),
    staleTime: 5 * 60_000,
  });

  const items = ranking.data?.items ?? [];
  const totalPaginas = Math.max(1, Math.ceil((ranking.data?.total ?? 0) / (ranking.data?.pageSize ?? 25)));

  return (
    <AppShell
      title="Inteligencia competitiva"
      subtitle="Quiénes adjudican, dónde y con qué intensidad — datos oficiales de Mercado Público"
    >
      <div className="rounded-xl border border-border bg-card p-4 mb-4 flex flex-col md:flex-row gap-3 md:items-center">
        <form
          className="relative flex-1"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setBusqueda(search.trim());
          }}
        >
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar competidor por nombre o RUT"
            className="pl-9"
            aria-label="Buscar competidor"
          />
        </form>
        <Select
          value={ventana}
          onValueChange={(v) => {
            setPage(1);
            setVentana(v as CompetidorVentana);
          }}
        >
          <SelectTrigger className="md:w-52" aria-label="Ventana temporal">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VENTANAS.map((v) => (
              <SelectItem key={v.value} value={v.value}>
                {v.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={orden}
          onValueChange={(v) => {
            setPage(1);
            setOrden(v as CompetidorOrden);
          }}
        >
          <SelectTrigger className="md:w-56" aria-label="Ordenar por">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ORDENES.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={tipo}
          onValueChange={(v) => {
            setPage(1);
            setTipo(v as TipoCompetidores);
          }}
        >
          <SelectTrigger className="md:w-44" aria-label="Tipo de oportunidad">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIPOS.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {ranking.isError ? (
        <EmptyState
          icon={AlertTriangle}
          title="No pudimos cargar el ranking de competidores"
          action={
            <Button size="sm" onClick={() => ranking.refetch()}>
              Reintentar
            </Button>
          }
        />
      ) : ranking.isLoading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-36 rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-96 rounded-xl" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Users2}
          title="Sin adjudicaciones en esta ventana"
          description="Amplía el rango de fechas o revisa que la sincronización haya traído actas de adjudicación."
          action={
            <Button size="sm" variant="outline" onClick={() => setVentana("todo")}>
              Ver todo el histórico
            </Button>
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {items.slice(0, 3).map((c, i) => (
              <Link
                key={c.id}
                to="/competidores/$id"
                params={{ id: c.id }}
                className="rounded-xl border border-border bg-card p-5 shadow-elegant hover:border-primary/40 transition block"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="size-10 rounded-lg bg-gradient-primary grid place-items-center text-primary-foreground font-bold shadow-glow">
                    #{i + 1}
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-warning/15 text-warning font-semibold">
                    Agresividad {c.scoreAgresividad}
                  </span>
                </div>
                <h3 className="font-display font-semibold text-foreground truncate">{c.nombre}</h3>
                <div className="text-xs text-muted-foreground mb-3">RUT {c.rut}</div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-sm font-semibold tabular-nums">{c.adjudicadas}</div>
                    <div className="text-[10px] text-muted-foreground">Adjudic.</div>
                  </div>
                  <div>
                    <div className="text-sm font-semibold tabular-nums text-primary">
                      {c.participacion}%
                    </div>
                    <div className="text-[10px] text-muted-foreground">Share</div>
                  </div>
                  <div>
                    <div className="text-sm font-semibold tabular-nums text-success">
                      {formatCLP(c.montoAdjudicado)}
                    </div>
                    <div className="text-[10px] text-muted-foreground">Adjudicado</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2 rounded-xl border border-border bg-card overflow-hidden shadow-elegant">
              <div className="p-5 border-b border-border flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-display font-semibold flex items-center gap-2">
                    <Trophy className="size-4 text-primary" /> Ranking de competidores
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {ranking.data?.total ?? 0} proveedores · {formatCLP(ranking.data?.totalAdjudicado ?? 0)} adjudicados
                  </p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium px-4 py-2.5">Competidor</th>
                      <th className="text-right font-medium px-4 py-2.5">Adjudic.</th>
                      <th className="text-right font-medium px-4 py-2.5">Monto</th>
                      <th className="text-right font-medium px-4 py-2.5">Share</th>
                      <th className="text-left font-medium px-4 py-2.5 hidden md:table-cell">
                        Categorías
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {items.map((c) => (
                      <tr key={c.id} className="hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="min-w-0">
                              <Link
                                to="/competidores/$id"
                                params={{ id: c.id }}
                                className="font-medium text-foreground hover:text-primary"
                              >
                                {c.nombre}
                              </Link>
                              <div className="text-[11px] text-muted-foreground">{c.rut}</div>
                            </div>
                            <SeguirCriterioButton
                              variant="ghost"
                              label=""
                              opciones={[{ kind: "competidor", valor: c.nombre, label: "Competidor" }]}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums">
                          {c.adjudicadas}
                        </td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums text-success">
                          {formatCLP(c.montoAdjudicado)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums text-primary">
                          {c.participacion}%
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {c.categoriasDominantes.slice(0, 2).map((x) => (
                              <span
                                key={x}
                                className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground"
                              >
                                {x}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPaginas > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-border text-xs">
                  <span className="text-muted-foreground">
                    Página {page} de {totalPaginas}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Anterior
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={page >= totalPaginas}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-border bg-card overflow-hidden shadow-elegant">
              <div className="p-5 border-b border-border">
                <h3 className="font-display font-semibold flex items-center gap-2">
                  <Building2 className="size-4 text-primary" /> Organismos top
                </h3>
                <p className="text-xs text-muted-foreground">Con quién compites más</p>
              </div>
              {organismos.isLoading ? (
                <div className="p-4 space-y-3">
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-14 rounded-lg" />
                  ))}
                </div>
              ) : (organismos.data ?? []).length === 0 ? (
                <p className="p-5 text-sm text-muted-foreground">
                  Sin adjudicaciones registradas en la ventana seleccionada.
                </p>
              ) : (
                <div className="divide-y divide-border">
                  {(organismos.data ?? []).map((o) => (
                    <div key={o.organismo} className="p-4">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="text-sm font-medium text-foreground truncate">
                          {o.organismo}
                        </div>
                        <span
                          className={cn(
                            "text-[10px] px-1.5 py-0.5 rounded-full font-medium capitalize",
                            o.tasaCompetencia === "alta"
                              ? "bg-destructive/15 text-destructive"
                              : o.tasaCompetencia === "media"
                                ? "bg-warning/15 text-warning"
                                : "bg-success/15 text-success",
                          )}
                        >
                          {o.tasaCompetencia}
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        Top: {o.topCompetidor}
                        {o.promedioOferentes > 0 && ` · ${o.promedioOferentes} oferentes prom.`}
                      </div>
                      <div className="flex items-center justify-between mt-1.5 text-xs">
                        <span className="text-muted-foreground">
                          {o.totalLicitaciones} licitaciones
                        </span>
                        <span className="font-mono tabular-nums text-primary inline-flex items-center gap-1">
                          <TrendingUp className="size-3" /> {formatCLP(o.montoAdjudicado)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </AppShell>
  );
}
